import {type ModInfo} from './modsDetector';
import path from 'path';
import type fs from 'fs';
import {promisify} from 'util';
import * as process from 'process'

export function sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

const emulatorsList = new Set<string>(['joiplay', 'maldives']);
export function isAndroid() {
    return !!process.env.USER && emulatorsList.has(process.env.USER);
}

export function getDetailedName(mod: ModInfo) {
    return mod.parameters.displayedName
        ? `'${mod.parameters.displayedName}' (${mod.name})`
        : `'${mod.name}'`;
}

export function resolvePath(...localPath: string[]) {
    const rootFolder = path.dirname(process.mainModule?.filename || '');
    return path.resolve(rootFolder, ...localPath);
}

export function getRelativePath(absolutePath: string) {
    return path.relative(
        path.dirname(process.mainModule?.filename || ''),
        resolvePath(absolutePath)
    );
}

export function toUrl(filePath: string) {
    return getUniversalPath(getRelativePath(filePath))
}

export function getUniversalPath(location: string): string {
    return path.sep === path.posix.sep
        ? location
        : location.split(path.sep).join(path.posix.sep);
}

export async function queryFile(filePath: string) {
    const response = await fetch(toUrl(filePath));
    return await response.text();
}

export function ensureFsFunction<K extends keyof typeof fs>(name: K): typeof fs[K] {
    const func = require('fs')[name];
    if (typeof func !== 'function') {
        throw new Error(`Function '${name}' is not supported in fs API.`);
    }
    return func;
}

export async function getReaddirAsync() {
    return (await import('fs'))?.promises?.readdir
        ?? promisify(ensureFsFunction('readdir'));
}

export async function getExistsSync() {
    return (await import('fs')).existsSync;
}

export function isBrowser() {
    return (typeof globalThis.require !== 'function') || (typeof process !== 'object');
}

type FsSyncFunc =
    (() => any) |
    (() => void) |
    ((arg1: any) => any) |
    ((arg1: any) => void) |
    ((arg1: any, arg2: any) => any) |
    ((arg1: any, arg2: any) => void) |
    ((arg1: any, arg2: any, arg3: any) => any) |
    ((arg1: any, arg2: any, arg3: any) => void);

type FsCallbackFunc<TFsSyncFunc extends FsSyncFunc> =
    TFsSyncFunc extends () => infer TResult
        ? ((callback: (err: any, result: TResult) => void) => void)
        : TFsSyncFunc extends () => void
            ? ((callback: (err?: any) => void) => void)

            : TFsSyncFunc extends (arg1: infer T1) => void
                ? ((arg1: T1, callback: (err?: any) => void) => void)
                : TFsSyncFunc extends (arg1: infer T1) => infer TResult
                    ? ((arg1: T1, callback: (err: any, result: TResult) => void) => void)

                    : TFsSyncFunc extends (arg1: infer T1, arg2: infer T2) => infer TResult
                        ? ((arg1: T1, arg2: T2, callback: (err: any, result: TResult) => void) => void)
                        : TFsSyncFunc extends (arg1: infer T1, arg2: infer T2) => void
                            ? ((arg1: T1, arg2: T2, callback: (err?: any) => void) => void)

                            : TFsSyncFunc extends (arg1: infer T1, arg2: infer T2, arg3: infer T3) => infer TResult
                                ? ((arg1: T1, arg2: T2, arg3: T3, callback: (err: any, result: TResult) => void) => void)
                                : TFsSyncFunc extends (arg1: infer T1, arg2: infer T2, arg3: infer T3) => void
                                    ? ((arg1: T1, arg2: T2, arg3: T3, callback: (err?: any) => void) => void)

                                    : never;

type FsAsyncFunc<TFsFunc extends FsSyncFunc> = TFsFunc extends (...args: infer TArgs) => infer TResult
    ? ((...args: TArgs) => Promise<TResult>)
    : never;

export function getFsAsync<TFsSyncFunc extends FsSyncFunc>(
    getAsyncFs: () => (FsAsyncFunc<TFsSyncFunc> | undefined),
    getCallbackFs: () => (FsCallbackFunc<TFsSyncFunc> | undefined),
    getSyncFs: () => (TFsSyncFunc | undefined)
): FsAsyncFunc<TFsSyncFunc> {
    const asyncFs = getAsyncFs();
    if (asyncFs) {
        return asyncFs;
    }

    console.warn(
        `Promise version of function '${getAsyncFs}' is not supported in current fs API. ` +
        'Falling back to callback implementation.'
    );
    const callbackFs = getCallbackFs();
    if (callbackFs) {
        return promisify(getCallbackFs) as FsAsyncFunc<TFsSyncFunc>;
    }

    console.warn(
        `Callback version of function '${getCallbackFs}' is not supported in current fs API. ` +
        'Falling back to sync implementation.'
    );

    const syncFunction = getSyncFs();
    if (!syncFunction) {
        throw new Error(`Function '${getSyncFs}' is not supported in fs API.`)
    }

    return ((...args: []) => {
        return new Promise((resolve, reject) => {
            try {
                const result = (syncFunction as any).apply({}, args);
                resolve(result);
            } catch (error) {
                reject(error);
            }
        });
    }) as FsAsyncFunc<TFsSyncFunc>;
}
